import { DWS } from '@interfaces/dws';
import { AdminNotificationDetail } from '@interfaces/dws/admin-notification';

/** Sort wineries alphabetically by name, then by id. */
export function sortWineriesAlphabetically(wineries: DWS.Winery[]): DWS.Winery[] {
  return [...wineries].sort((a, b) => {
    const nameCmp = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    if (nameCmp !== 0) {
      return nameCmp;
    }
    return String(a.id).localeCompare(String(b.id));
  });
}

export function formatWineryLabel(winery: DWS.Winery): string {
  return `${winery.name} (ID: ${winery.id})`;
}

export function wineryIdsFromSelection(items: Array<{ id?: string; selected?: boolean }>): string[] {
  return items
    .filter(item => item.selected && item.id != null)
    .map(item => String(item.id));
}

export function selectedWineriesFromIds(wineries: DWS.Winery[], ids: string[]): DWS.Winery[] {
  if (!ids || !ids.length) {
    return [];
  }
  const idSet = new Set(ids.map(String));
  return wineries.filter(w => idSet.has(String(w.id)));
}

/** Resolves winery IDs from API detail + stored payload (supports legacy single wineryId). */
export function resolveWineryIdsFromDetail(
  detail: Pick<AdminNotificationDetail, 'toAll' | 'companyIds'>,
  payload: { target?: { wineryIds?: string[]; wineryId?: string | null }; companyIds?: string[] } | null | undefined
): string[] {
  if (detail.toAll) {
    return [];
  }
  if (detail.companyIds && detail.companyIds.length) {
    return detail.companyIds.map(String);
  }
  const target = payload && payload.target;
  if (target && target.wineryIds && target.wineryIds.length) {
    return target.wineryIds.map(String);
  }
  if (target && target.wineryId != null && target.wineryId !== '') {
    return [String(target.wineryId)];
  }
  if (payload && payload.companyIds && payload.companyIds.length) {
    return payload.companyIds.map(String);
  }
  return [];
}

type BannerLanguageContent = {
  showCta?: boolean;
  ctaText?: string;
};

/** CTA label for a language: own text, then English, then any other language that has one. */
export function resolveBannerCtaLabel(
  multilingualContent: Record<string, BannerLanguageContent> | null | undefined,
  language: string
): string {
  if (!multilingualContent) {
    return '';
  }
  const entries = Object.values(multilingualContent);
  if (!entries.some(entry => entry?.showCta)) {
    return '';
  }
  const candidates = [
    multilingualContent[language]?.ctaText,
    multilingualContent['en']?.ctaText,
    ...entries.map(entry => entry?.ctaText),
  ];
  for (const value of candidates) {
    const text = (value ?? '').toString().trim();
    if (text) {
      return text;
    }
  }
  return '';
}

export function buildTargetPayload(targetType: string, wineryIds: string[]): Record<string, unknown> {
  if (targetType === 'all') {
    return { type: 'all' };
  }
  return {
    type: 'specific',
    wineryIds: wineryIds || [],
    wineryId: wineryIds && wineryIds.length === 1 ? wineryIds[0] : null,
  };
}
